import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, Icon, IconButton, Typography } from '@material-ui/core';
import styled from 'styled-components';
import NumberFormat from 'react-number-format';
import ModalWrapper from '../../../../../../components/ModalWrapper';
import { roles } from '../../../../../../constants';
import { change } from 'redux-form';
import ConfirmModal from 'components/Modals/ConfirmModal';
import { modalsHide, modalsToggle, snackShow } from 'actions';
import { useGetAdditionalBidText } from '../hooks';
import { EditBidsForm } from './EditBidsForm';
import { useActions } from './useActions';
import { cutTextElipsis, convertToLocalTime } from 'utils';
import { useUnmarkVehicleAsVisibleMutation } from 'services/api/admin-auction-vehicles';
import toast from 'react-hot-toast';

const StyledModalWrapper = styled(ModalWrapper)`
  > div:nth-child(2) {
    max-width: 715px;
    width: 100%;
  }
`;

const TooltipWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  width: 650px;
  gap: 24px;
  max-height: 400px;
  overflow: auto;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const Title = styled.div`
  font-size: 1.3125rem;
  color: #000;
  font-weight: 500;
`;

const configByType = {
  bids: {
    title: 'All Bids',
    modalId: 'view-all-bids',
    editButton: 'Edit Bids',
    deleteButton: 'Delete all bids',
    log: 'Bid Log',
    confirmDeleteAll: 'Are you sure you want to remove all bids?'
  },
  offers: {
    title: 'All Offers',
    modalId: 'view-all-offers',
    editButton: 'Edit Offers',
    deleteButton: 'Delete all offers',
    log: 'Highest Offer',
    confirmDeleteAll: 'Are you sure you want to remove all offers?'
  },
  alternativeOffers: {
    title: 'All Offers',
    modalId: 'view-all-alternative-offers',
    editButton: 'Edit Offers',
    deleteButton: 'Delete all offers',
    log: 'Highest Offer',
    confirmDeleteAll: 'Are you sure you want to remove all offers?'
  }
};

const RenderList = ({ bids }) => {
  const user = useSelector(state => state.user.user);
  return (
    <Grid container direction="column" style={{ overflowY: 'scroll', maxHeight: 300, flexWrap: 'nowrap' }}>
      {bids.map((bid, index) => (
        <Grid container item xs={12} key={index} alignItems="center">
          <Grid item xs={6} sm={2} style={{ flexBasis: '10%', maxWidth: '10%' }}>
            <NumberFormat displayType="text" prefix="$" value={bid.bid} thousandSeparator />
          </Grid>
          <Grid item xs={6} sm={4} style={{ flexBasis: '65%', maxWidth: '65%' }}>
            <Typography style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{`by ${
              bid.bidder
            } ${bid.admin && user?.role === 'admin' ? `(AA ${bid.admin.first_name})` : ''}`}</Typography>
          </Grid>
          <Grid item xs={6} sm={6} style={{ flexBasis: '25%', maxWidth: '25%' }}>
            <Typography>{bid.date}</Typography>
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};

export const ViewAllBidsModalFC = ({ type, auctionType, bids, proxyBid, vehicleId, editable, startingBid }) => {
  const dispatch = useDispatch();

  const formName = `adminEditBidModalForm-${type}`;

  const user = useSelector(state => state.user.user);

  const { removeAllBids } = useActions(type);

  const [isFormToggled, setIsFormToggled] = useState(false);

  useEffect(() => {
    if (type !== 'bids') return;
    const [lastBid] = bids;
    if (lastBid) dispatch(change(formName, 'bid_amount', lastBid.bid));
  }, [bids?.length, dispatch, type, formName]);

  const initForm = () => {
    const [lastBid] = bids;
    if (lastBid) dispatch(change(formName, 'bid_amount', lastBid.bid));
    if (proxyBid) dispatch(change(formName, 'proxy_bid_amount', proxyBid.amount));
  };

  const toggleForm = () => setIsFormToggled(curr => !curr);

  const hideForm = () => {
    if (isFormToggled) {
      setIsFormToggled(false);
    }
  };

  const getAdditionalBidText = useGetAdditionalBidText(auctionType, startingBid);

  const renderList = () => {
    return (
      <Grid container direction="column">
        {bids.map((bid, index) => {
          const { text, color } = getAdditionalBidText(bid);
          return (
            <Grid container item xs={12} key={index} alignItems="center">
              <Grid item xs={6} sm={2}>
                <NumberFormat displayType="text" prefix="$" value={bid.bid} thousandSeparator />
              </Grid>
              <Grid item xs={6} sm={6}>
                <Typography style={{ lineHeight: 'normal', display: 'flex', gap: 4 }}>
                  {`by ${cutTextElipsis(bid.bidder, 22)} ${
                    bid.admin && user?.role === 'admin' ? `(AA ${bid.admin.first_name})` : ''
                  }`}
                  {text ? <div style={{ color }}>{`(${text})`}</div> : ''}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography>{bid.date}</Typography>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const userRole = useSelector(state => state?.user?.user?.role);
  const userJob = useSelector(state => state?.user?.user?.job_title);

  const { title, modalId, editButton, deleteButton, confirmDeleteAll } = configByType[type];

  const cancelText = (() => {
    if (userRole !== roles.ADMIN || !editable) {
      return 'Ok';
    }
    return isFormToggled ? 'Done' : editButton;
  })();

  const handleRemoveAll = useCallback(async () => {
    try {
      const { error } = await removeAllBids({ vehicleId });
      if (!error) {
        dispatch(snackShow({ type: 'error', message: `Removed all ${type}` }));
        window.location.reload();
      } else {
        dispatch(snackShow({ type: 'error', message: `Failed to remove all ${type}` }));
      }
    } catch (error) {
      dispatch(snackShow({ type: 'error', message: `Failed to remove all ${type}` }));
    }
    dispatch(modalsToggle(`remove-${modalId}`));
  }, []);

  const openRemoveModal = () => {
    dispatch(modalsHide(modalId));
    dispatch(modalsToggle(`remove-${modalId}`));
  };

  return (
    <>
      <StyledModalWrapper
        title={title}
        modalId={modalId}
        cancelText={cancelText}
        showAdditionalButton={
          userRole === roles.ADMIN && editable && ['Admin', 'Manager'].includes(userJob) && !isFormToggled
        }
        additionalButtonText={deleteButton}
        additionalButtonAction={openRemoveModal}
        showSubmit={false}
        onEnter={initForm}
        cancelCustomAction={userRole === roles.ADMIN && editable ? toggleForm : null}
        onClose={hideForm}
      >
        {roles.ADMIN === userRole && isFormToggled ? (
          <EditBidsForm vehicleId={Number(vehicleId)} bids={bids} proxyBid={proxyBid} type={type} />
        ) : (
          renderList()
        )}
      </StyledModalWrapper>
      <ConfirmModal
        title={confirmDeleteAll}
        handleSubmit={handleRemoveAll}
        submitText={'Delete'}
        modalId={`remove-${modalId}`}
      />
    </>
  );
};

export const ViewAllBidsTooltip = ({ type, bids, proxyBid, vehicleId, editable, close = () => {}, visibleBuyers = [] }) => {
  const dispatch = useDispatch();

  const formName = `adminEditBidModalForm-${type}`;

  const { removeAllBids } = useActions(type);
  const [unmarkVisible] = useUnmarkVehicleAsVisibleMutation();

  const [isFormToggled, setIsFormToggled] = useState(false);

  useEffect(() => {
    if (type !== 'bids') return;
    const [lastBid] = bids;
    if (lastBid) dispatch(change(formName, 'bid_amount', lastBid.bid));
  }, [bids?.length, dispatch, type, formName]);

  const toggleForm = () => setIsFormToggled(curr => !curr);

  const toggleWithInit = () => {
    toggleForm();
  };

  const userRole = useSelector(state => state?.user?.user?.role);
  const userJob = useSelector(state => state?.user?.user?.job_title);

  const { title, modalId, editButton, deleteButton, confirmDeleteAll } = configByType[type];

  const cancelText = (() => {
    if (userRole !== roles.ADMIN || !editable) {
      return 'Ok';
    }
    return isFormToggled ? 'Done' : editButton;
  })();

  const handleRemoveAll = useCallback(async () => {
    try {
      const { error } = await removeAllBids({ vehicleId });
      if (!error) {
        dispatch(snackShow({ type: 'error', message: 'Removed all bids' }));
        window.location.reload();
      } else {
        dispatch(snackShow({ type: 'error', message: 'Failed to remove all bids' }));
      }
    } catch (error) {
      dispatch(snackShow({ type: 'error', message: 'Failed to remove all bids' }));
    }
    dispatch(modalsToggle(`remove-${modalId}`));
  }, []);

  const openRemoveModal = () => {
    dispatch(modalsHide(modalId));
    dispatch(modalsToggle(`remove-${modalId}`));
  };

  const handleDeleteVisibleBuyer = async buyer => {
    if (!window.confirm(`Are you sure you want to remove ${buyer.user?.dealership_name}'s visibility?`)) return;
    try {
      await unmarkVisible({ vehicleId: Number(vehicleId), userIds: [buyer.user_id] });
    } catch {
      toast.error('Failed to remove visibility');
    }
  };

  const showVisibleBuyers = visibleBuyers.length > 0;

  return (
    <TooltipWrapper>
      <Title>{title}</Title>
      {roles.ADMIN === userRole && editable && isFormToggled ? (
        <EditBidsForm vehicleId={Number(vehicleId)} bids={bids} proxyBid={proxyBid} type={type} renderType="popup" />
      ) : (
        <RenderList bids={bids} />
      )}
      {showVisibleBuyers && (
        <>
          <hr style={{ border: 'none', borderTop: '1px solid rgba(0,0,0,0.12)', margin: '8px 0' }} />
          <Grid container direction="column">
            {visibleBuyers.map((buyer, index) => (
              <Grid container item xs={12} key={index} alignItems="center" style={{ flexWrap: 'nowrap' }}>
                <Grid item style={{ flexBasis: '10%', maxWidth: '10%' }}>
                  <span>-</span>
                </Grid>
                <Grid item style={{ flexBasis: editable && userRole === roles.ADMIN && userJob === 'Admin' ? '55%' : '65%', maxWidth: editable && userRole === roles.ADMIN && userJob === 'Admin' ? '55%' : '65%', minWidth: 0 }}>
                  <Typography style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                    {`by ${buyer.user?.dealership_name} (${buyer.creator?.first_name})`}
                  </Typography>
                </Grid>
                <Grid item style={{ flexBasis: '25%', maxWidth: '25%' }}>
                  <Typography>{buyer.created_at ? convertToLocalTime(buyer.created_at, true) : ''}</Typography>
                </Grid>
                {editable && userRole === roles.ADMIN && userJob === 'Admin' && (
                  <Grid item style={{ flexBasis: '10%', maxWidth: '10%', display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton onClick={() => handleDeleteVisibleBuyer(buyer)} style={{ padding: 4 }}>
                      <Icon style={{ fontSize: 18 }}>delete</Icon>
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            ))}
          </Grid>
        </>
      )}
      <Actions>
        {!isFormToggled && editable && userRole === 'admin' && ['Admin', 'Manager'].includes(userJob) && (
          <Button onClick={openRemoveModal} color="primary" variant="outlined">
            {deleteButton}
          </Button>
        )}
        <Button
          onClick={userRole === roles.ADMIN && editable ? toggleWithInit : close}
          color="primary"
          variant="outlined"
        >
          {cancelText}
        </Button>
      </Actions>
      <ConfirmModal
        title={confirmDeleteAll}
        handleSubmit={handleRemoveAll}
        submitText={'Delete'}
        modalId={`remove-${modalId}`}
      />
    </TooltipWrapper>
  );
};

export default ViewAllBidsModalFC;

{
  /* <RenderForm
          formName={formName}
          bids={bids}
          handleSubmit={handleSubmit}
          proxyBid={proxyBid}
          removeBid={removeBid}
          initForm={initForm}
          removeProxyBid={removeProxyBid}
          submitChangeWithType={submitChangeWithType}
          type={type}
        /> */
}
